import React, { useState, useEffect } from 'react';
import { FaTimes, FaUserFriends, FaCheckCircle, FaClock, FaEye, FaTimesCircle } from 'react-icons/fa';
import './FamilyAssociationModal.css';
import API_BASE_URL from '../../config/api';

const API_BASE = API_BASE_URL;

const FamilyAssociationModal = ({ isOpen, onClose }) => {
    const [form, setForm] = useState({
        familyMemberName: '',
        familyMemberNif: '',
        familyMemberBirthDate: '',
        requesterMessage: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [myRequests, setMyRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchMyRequests();
            setSubmitted(false);
            setError('');
            setForm({ familyMemberName: '', familyMemberNif: '', familyMemberBirthDate: '', requesterMessage: '' });
        }
    }, [isOpen]);

    const fetchMyRequests = async () => {
        setLoadingRequests(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/api/family-associations/my-requests`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMyRequests(data);
            }
        } catch (e) {
            console.error('Error fetching requests:', e);
        } finally {
            setLoadingRequests(false);
        }
    };

    const handleChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.familyMemberName.trim()) {
            setError('O nome do familiar é obrigatório.');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/api/family-associations/request`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    familyMemberName: form.familyMemberName,
                    familyMemberNif: form.familyMemberNif || null,
                    familyMemberBirthDate: form.familyMemberBirthDate || null,
                    requesterMessage: form.requesterMessage || null
                })
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.message || 'Erro ao enviar requisição');
            }

            setSubmitted(true);
            fetchMyRequests();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('pt-PT');
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case 'Accepted': return { label: 'Aceite', icon: <FaCheckCircle />, className: 'status-accepted' };
            case 'Rejected': return { label: 'Recusado', icon: <FaTimesCircle />, className: 'status-rejected' };
            case 'Seen': return { label: 'Visto', icon: <FaEye />, className: 'status-seen' };
            default: return { label: 'Pendente', icon: <FaClock />, className: 'status-pending' };
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fam-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="fam-modal">
                <div className="fam-modal-header">
                    <div className="fam-modal-title">
                        <FaUserFriends />
                        <h2>Associar Familiar</h2>
                    </div>
                    <button className="fam-modal-close" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="fam-modal-body">
                    {!submitted ? (
                        <form onSubmit={handleSubmit} className="fam-form">
                            <p className="fam-form-description">
                                Preenche os dados do familiar inscrito no clube que pretendes associar à tua conta.
                                O administrador irá processar o pedido.
                            </p>

                            <div className="fam-form-group">
                                <label htmlFor="familyMemberName">Nome Completo *</label>
                                <input
                                    id="familyMemberName"
                                    name="familyMemberName"
                                    type="text"
                                    placeholder="Nome do familiar"
                                    value={form.familyMemberName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="fam-form-row">
                                <div className="fam-form-group">
                                    <label htmlFor="familyMemberNif">NIF</label>
                                    <input
                                        id="familyMemberNif"
                                        name="familyMemberNif"
                                        type="text"
                                        placeholder="123456789"
                                        maxLength={9}
                                        value={form.familyMemberNif}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="fam-form-group">
                                    <label htmlFor="familyMemberBirthDate">Data de Nascimento</label>
                                    <input
                                        id="familyMemberBirthDate"
                                        name="familyMemberBirthDate"
                                        type="date"
                                        value={form.familyMemberBirthDate}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="fam-form-group">
                                <label htmlFor="requesterMessage">Mensagem (opcional)</label>
                                <textarea
                                    id="requesterMessage"
                                    name="requesterMessage"
                                    rows={3}
                                    placeholder="Informações adicionais que ajudem o admin a identificar o familiar..."
                                    value={form.requesterMessage}
                                    onChange={handleChange}
                                />
                            </div>

                            {error && <div className="fam-error-alert">{error}</div>}

                            <div className="fam-form-actions">
                                <button type="button" className="fam-btn-cancel" onClick={onClose}>
                                    Cancelar
                                </button>
                                <button type="submit" className="fam-btn-submit" disabled={submitting}>
                                    {submitting ? 'A enviar...' : 'Enviar Pedido'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="fam-success-container">
                            <FaCheckCircle className="fam-success-icon" />
                            <h3>Pedido Enviado!</h3>
                            <p>
                                O teu pedido para associar <strong>{form.familyMemberName}</strong> foi submetido com sucesso.
                                Receberás confirmação assim que for revisto pela administração do clube.
                            </p>
                            <button className="fam-btn-submit" onClick={() => setSubmitted(false)}>
                                Fazer Outro Pedido
                            </button>
                        </div>
                    )}

                    {myRequests.length > 0 && (
                        <div className="fam-history-section">
                            <h4>Os Teus Pedidos Anteriores</h4>
                            <div className="fam-history-list">
                                {myRequests.map((req) => {
                                    const statusInfo = getStatusInfo(req.status);
                                    return (
                                        <div key={req.id} className="fam-history-item">
                                            <div className="fam-history-info">
                                                <span className="fam-history-name">{req.familyMemberName}</span>
                                                <span className="fam-history-date">
                                                    Submetido a {formatDate(req.requestedAt)}
                                                </span>
                                            </div>
                                            <div className={`fam-status-badge ${statusInfo.className}`}>
                                                {statusInfo.icon}
                                                <span>{statusInfo.label}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FamilyAssociationModal;
